/**
 * Shared DataTable Types
 *
 * Type definitions shared between client and bcc DataTable implementations.
 */

import type { PaginationState, RowSelectionState } from "@tanstack/react-table";

/**
 * Row tone/status indicator for visual styling
 */
export type DataTableRowTone = "subdued" | "success" | "warning" | "critical" | "canceled";

/**
 * Extended row type with DataTable-specific metadata
 */
export type DataTableRow<T> = T & {
  /** Shows loading indicator in the row */
  isLoading?: boolean;
  /** Disables row selection and interactions */
  isDisabled?: boolean;
  /** Shows error indicator in the row */
  hasError?: boolean;
  /** Error details for the row */
  errors?: Error[];
  /** Visual tone/status indicator */
  tone?: DataTableRowTone;
};

/**
 * Filter configuration for DataTable
 */
export interface DataTableFilter {
  /** Display label for the filter */
  label: string;
  /** Filter value(s), comma-separated for multiple */
  value?: string;
  /** Whether the filter is currently selected */
  selected?: boolean;
  /** Column ID to filter on */
  column?: string;
  /** Whether this filter should be selected by default */
  defaultSelected?: boolean;
  /** Custom filter function */
  filterFn?: () => void;
  /** When true, the filter button is visually disabled and not clickable */
  disabled?: boolean;
}

/**
 * Extended pagination state with cursor support
 */
export interface DataTablePagination extends PaginationState {
  /** Previous page cursor for cursor-based pagination */
  prevCursor?: string;
  /** Next page cursor for cursor-based pagination */
  nextCursor?: string;
  /** Total count of items */
  total?: number;
}

/**
 * Bulk action configuration
 */
export interface BulkAction {
  /** Button label */
  label: string;
  /** Click handler receiving current selection */
  onClick: (selection: RowSelectionState) => void;
  /** Dynamic state based on selection */
  state?: (selection: RowSelectionState) => "loading" | "disabled" | "pressed" | "selected" | undefined;
}

/**
 * Promoted action configuration (always visible actions)
 */
export interface PromotedAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
}

/**
 * Column header cell configuration
 */
export interface DataTableHeaderCellProps {
  /** Header label (string or React node) */
  label: string | React.ReactNode;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Whether the column is sortable */
  sortable?: boolean;
}

/**
 * Default pagination configuration
 */
export const DEFAULT_PAGE_INDEX = 0;
export const DEFAULT_TABLE_PAGE_SIZE = 2000;

/**
 * Default pagination state
 */
export const DEFAULT_PAGINATION: PaginationState = {
  pageIndex: DEFAULT_PAGE_INDEX,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
};
